import { ColumnConfig, CustomColDef } from "./SnGrid_Types";
import { getCodeData } from "@shared/utils/CommonUtils";
import { PassEditCellEditor, DateBoxCellEditor } from "./SnGrid_editors";
import dayjs from "dayjs";
import {
  ITooltipParams,
  ColDef,
  CellStyleFunc,
} from "ag-grid-charts-enterprise";
import ButtonBoxCellEditor from "@shared/components/Grid/ButtonBoxCellEditor";
import MaskEditCellEditor from "@shared/components/Grid/MaskEditCellEditor";

// 유형별 컬럼 설정
const getColumnTypeConfig = async (
  column: ColumnConfig,
  user: any
): Promise<Partial<CustomColDef>> => {
  switch (column.tp_column) {
    case "SnTextBox":
      return {
        cellEditor: "agTextCellEditor",
        filter: "agTextColumnFilter",
        // 필수값 표시 스타일
        // cellStyle: (params) => {
        //   if (column.yn_required === "Y" && !params.node?.rowPinned) {
        //     return { backgroundColor: "#ffcccc" }; // 입력이 없으면 테두리 강조
        //   }
        // },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };

    case "SnNumberBox":
      return {
        cellEditor: "agNumberCellEditor",
        filter: "agNumberColumnFilter",

        valueFormatter: (params) => {
          // if (!params.value) return "0";
          if (!params.value || isNaN(params.value)) return "";

          // 숫자 포맷 (천 단위 구분)
          return Number(params.value).toLocaleString();
        },
        cellStyle: (params) => {
          return { textAlign: "right" }; // 오른쪽 정렬
        },
        suppressKeyboardEvent: (params: any) => {
          const key = params.event.key;

          // 숫자 증감만 막고, 셀 포커스 이동은 허용 (20250701)
          if (key === "ArrowUp" || key === "ArrowDown") {
            const isInputFocused = params.event.target?.tagName === "INPUT";
            if (isInputFocused) {
              params.event.preventDefault();
            }

            return false;
          }

          return false;
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };

    case "SnMaskEdit": {
      return {
        cellEditor: MaskEditCellEditor, // 커스텀 마스크 에디터 사용
        cellEditorParams: {
          maskType: column.maskType, // 마스크 유형 전달
        },
        valueSetter: (params) => {
          if (params.colDef && params.colDef.field) {
            // field가 존재하는지 확인
            params.data[params.colDef.field] = params.newValue; // 값 설정

            return true;
          }
          return false; // 설정 실패 시 false 반환
        },
        valueGetter: (params) => {
          if (params.colDef && params.colDef.field) {
            return params.data[params.colDef.field] || ""; // 현재 데이터 반환
          }
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };
    }

    case "SnDateBox": {
      return {
        cellEditor: DateBoxCellEditor,

        valueFormatter: (params) => {
          const value = params.value;
          return value ? dayjs(value, "YYYYMMDD").format("YYYY/MM/DD") : "";
        },
        cellStyle: {
          textAlign: "center", // 가운데 정렬
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };
    }

    case "SnCheckBox": {
      return {
        // cellRenderer: (params: { value: string }) =>
        //   params.value === "Y" ? "✔" : "✖", // 렌더링 시 체크/언체크 표시
        // cellRenderer: "agCheckboxCellRenderer", // 기본 체크박스 렌더러 사용
        cellRendererSelector: (params: any) => {
          if (params.node?.rowPinned) {
            return { component: "" }; // footer는 아무것도 안 보여줌
          }
          return { component: "agCheckboxCellRenderer" };
        },
        cellEditor: "agCheckboxCellEditor", // 체크박스 편집기 사용
        cellEditorParams: {
          trueValue: "Y", // 체크된 값
          falseValue: "N", // 체크 해제된 값
        },
        valueSetter: (params: any) => {
          // 체크 상태에 따라 데이터에 Y/N 값 설정
          if (params.colDef && params.colDef.field) {
            params.data[params.colDef.field] = params.newValue ? "Y" : "N";
            return true;
          }
          return false;
        },
        valueGetter: (params: any) => {
          if (params.node?.rowPinned) return undefined;
          // 데이터에서 Y/N 값을 기반으로 체크 상태 반환
          return params.data[params.colDef.field] === "Y";
        },
        cellStyle: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };
    }

    case "SnComboBox": {
      let dataSource: any[] = [];

      // js_combo 우선 사용 (문자열이면 안전하게 파싱)
      if (Array.isArray(column.js_combo)) {
        dataSource = column.js_combo;
      } else if (typeof column.js_combo === "string") {
        const str = column.js_combo.trim();
        if (str) {
          try {
            dataSource = JSON.parse(str);
          } catch (e) {
            console.error("SnComboBox js_combo JSON parse fail:", e);
            dataSource = [];
          }
        }
      }

      // js_combo가 비어 있고 cd_clas가 있으면 코드 테이블로 대체
      if ((!dataSource || dataSource.length === 0) && column.cd_clas) {
        try {
          const codes =
            (await getCodeData(column.cd_clas, column.dc_sql || "")) || [];
          if (Array.isArray(codes)) {
            dataSource = codes
              .map((c: any) => ({
                code: c.code ?? c.cd ?? c.id ?? "",
                name: c.name ?? c.nm ?? c.ds ?? c.label ?? c.text ?? "",
              }))
              .filter((c: any) => c.code !== "");
          }
        } catch (e) {
          console.error("SnComboBox code load failed:", e);
        }
      }

      return {
        cellEditor: "agRichSelectCellEditor", // Rich Select 에디터 사용
        cellEditorParams: (params: any) => {
          const key = params.colDef?.colId ?? params.colDef?.field ?? "";
          const base = params.context?.comboCache?.[key] ?? dataSource;

          return {
            values: () => {
              let filData = column.dc_filter || "";
              let filteredData;

              if (!filData) {
                filteredData = base;
              } else {
                // [필드]는 콤보 코드(orgData) 참조, {필드}는 행 값. 행에 키가 없어도 미치환 상태로 eval되지 않게 한다.
                const row = params.data ?? {};
                filData = filData
                  .replace(/\[(\w+)\]/g, "orgData.$1")
                  .replace(/\{(\w+)\}/g, (_: string, k: string) =>
                    String(row[k] ?? "")
                  );

                filteredData = base.filter((orgData: any) =>
                  new Function("orgData", `return ${filData}`)(orgData)
                );
              }
              return filteredData.map((item: { code: any }) => item.code);
            },
            //// 25.09.12 agRichSelectCellEditor에 포멧을 사용하지 않고 렌더로 사용하여 name, code 구조로 포멧을 지정할 수 있음.
            // formatValue: (value: string) => {
            //   const selected = dataSource.find(
            //     (item: { code: string }) => item.code === value
            //   );
            //   // return selected ? selected.name : value;
            //   return selected ? `${selected.name} [${selected.code}]` : value;
            // },
            cellRenderer: (editorParams: { value: string }) => {
              const selected = base.find(
                (item: { code: string }) =>
                  String(item.code) === String(editorParams.value)
              );
              return selected
                ? `${selected.name} [${selected.code}]`
                : editorParams.value;
            },
          };
        },
        cellRenderer: (params: { value: any; colDef: any; context: any }) => {
          const key = params.colDef?.colId ?? params.colDef?.field ?? "";
          const base = params.context?.comboCache?.[key] ?? dataSource;
          // 셀에 name 값 표시
          const selected = base.find(
            (item: { code: any }) => String(item.code) === String(params.value)
          );
          return selected ? selected.name : params.value;
        },
        valueGetter: (params: any) => {
          // 데이터에서 code 값 반환
          if (params.colDef && params.colDef.field) {
            return params.data?.[params.colDef.field] ?? "";
          }
          return "";
        },
        valueSetter: (params) => {
          // 드롭다운에서 선택된 값을 데이터에 저장
          if (params.newValue !== undefined && params.newValue !== null) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = params.newValue;
            }

            return true; // 값이 변경되었음을 알림
          }
          return false;
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
        // 필수값 표시 스타일
        // cellStyle: (params) => {
        //   if (column.yn_required === "Y" && !params.node?.rowPinned) {
        //     return { backgroundColor: "#fff3cd" };
        //   }
        // },
      };
    }

    case "SnCodeBox":
      return {
        cellRenderer: (params: {
          focused: any;
          data: { [x: string]: any };
        }) => {
          const rawValue = params.data?.[column.cd_column];
          const isPasswordColumn = column.cd_popup === "POPUP_PASSWORD";

          if (isPasswordColumn) {
            return !rawValue ? "" : "••••••••••";
          }

          if (column.cd_displaycolumn) {
            const displayValue = params.data?.[column.cd_displaycolumn] || "";
            return params.focused ? rawValue : displayValue;
          } else {
            return rawValue || "";
          }
        },
        filter: true,
        filterValueGetter: (params: any) =>
          params.data?.[params.colDef.field] ?? "",
        valueGetter: (params) => {
          // 데이터에서 code 값 반환
          if (params.colDef && params.colDef.field) {
            return params.data?.[params.colDef.field] ?? "";
          }
          return "";
        },
        valueSetter: (params) => {
          if (params.newValue !== undefined) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = params.newValue;
              return true;
            }
          } else if (params.oldValue !== undefined) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = params.oldValue;
              return true;
            }
          }
          return false;
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };

    case "SnMultiCodeBox":
      return {
        cellRenderer: (params: { focused: any; data: { [x: string]: any } }) =>
          params.focused
            ? params.data[column.cd_column]
            : params.data[column.cd_column],
        valueGetter: (params) => {
          // 데이터에서 code 값 반환
          if (params.colDef && params.colDef.field) {
            return params.data?.[params.colDef.field] ?? "";
          }
          return "";
        },
        valueSetter: (params) => {
          if (params.newValue !== undefined) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = params.newValue;
              return true;
            }
          } else if (params.oldValue !== undefined) {
            if (params.colDef && params.colDef.field) {
              params.data[params.colDef.field] = params.oldValue;
              return true;
            }
          }
          return false;
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };

    case "SnButtonBox":
      return {
        ...column,
        headerComponent: ButtonBoxCellEditor,
        headerClass: "sn-button-header",
        cellRendererSelector: (params) => {
          if (params.node?.rowPinned) {
            return { component: "" };
          }
          return { component: ButtonBoxCellEditor };
        },
        cellRenderer: ButtonBoxCellEditor,
        cellStyle: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
      };

    case "SnPasswordBox": {
      return {
        cellEditor: PassEditCellEditor, // 커스텀 마스크 에디터 사용
        cellEditorParams: {
          maskType: "password", // 마스크 유형 전달
        },
        cellRenderer: (params: any) => {
          const value = params.value;
          // if (!value) return "";
          return "••••••••••";
        },
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };
    }

    default:
      return {
        cellEditor: "agTextCellEditor",
        filter: "agTextColumnFilter",
        editable: (params: any) => {
          const rowData = params.data;
          let cond = params.colDef.dc_editcon;
          if (cond == null || cond == "") return true;
          if (rowData.state === "insert") {
            cond = cond.replace("new", "false");
          } else {
            cond = cond.replace("new", "true");
          }
          Object.keys(rowData)
            .sort((a, b) => b.length - a.length)
            .forEach((key) => {
              cond = cond.replaceAll(key, rowData[key]);
            });
          return !new Function(`return (${cond})`)();
        },
      };
  }
};

let result: any = null;

// result 값을 외부로 export
export const setResult = (newResult: any) => {
  result = newResult;
};

export const getResult = () => result;

//  `CellStyleFunc`을 정확히 따르도록 타입 지정
export const getCellStyle = (
  errorMap: Map<number, { fields: Set<string>; messages: Map<string, string> }>
) => {
  return ((params) => {
    //  Footer 행인지 먼저 체크 → rowPinned가 true면 스타일 적용 안 함
    if (params.node?.rowPinned) {
      return null; //  Footer(합계 행)는 스타일 적용 제외!
    }

    if (!params.node || !params.colDef) return null;

    const rowIndex = params.node?.rowIndex ?? -1;
    const colDef = params.colDef as ColDef<any, any>;
    ///  기존 스타일 가져오기
    let baseStyle: any = {};

    //  `cellStyle`이 함수인 경우 실행
    if (typeof colDef.cellStyle === "function") {
      try {
        baseStyle = colDef.cellStyle(params) || {};
      } catch (error) {
        console.error("Error executing cellStyle function:", error);
        baseStyle = {};
      }
    }
    //  `cellStyle`이 객체라면 그대로 사용
    else if (
      typeof colDef.cellStyle === "object" &&
      colDef.cellStyle !== null
    ) {
      baseStyle = { ...colDef.cellStyle };
    }

    if (!colDef.field) return null; // 필드명이 없으면 무시

    // const errorInfo = errorMap.get(rowIndex);
    // if (errorInfo && errorInfo.fields.has(colDef.field)) {
    //   return { ...baseStyle, backgroundColor: "#ffcccc", color: "black" };
    // }

    return null;
  }) as CellStyleFunc<any, any>;
};

//  오류 메시지 툴팁 표시 함수
export const getTooltipValueGetter = (
  errorMap: Map<number, { fields: Set<string>; messages: Map<string, string> }>
) => {
  return ((params: ITooltipParams<any, any, any>) => {
    if (!params.node || !params.colDef) return null;

    const rowIndex = params.node.rowIndex ?? -1;
    // 'field' 속성이 존재하는 경우에만 처리
    if (!("field" in params.colDef) || !params.colDef.field) return null;

    const field = params.colDef.field ?? ""; // 기본값 제공

    if (!field) return null; // 필드명이 없으면 툴팁 표시 안 함

    const errorInfo = errorMap.get(rowIndex);

    if (errorInfo && errorInfo.messages.has(field)) {
      return errorInfo.messages.get(field) ?? ""; // 메시지가 없을 경우 빈 문자열 반환
    }

    return null;
  }) as (params: ITooltipParams<any, any, any>) => string | null;
};

// 공통: 코드→라벨 계산
// --- SnCombo 라벨 계산 ---
function getSnComboLabel(params: any, colDef: any) {
  const code = params.data?.[colDef.field];
  const nonEmpty = (v: any) => v !== undefined && v !== null && v !== "";

  let ep = colDef?.cellEditorParams;
  try {
    if (typeof ep === "function") {
      ep = ep({
        value: code,
        data: params.data,
        node: params.node,
        api: params.api,
        column: params.column,
        colDef,
      });
    }
  } catch {}

  if (ep && typeof ep.formatValue === "function") {
    try {
      const lbl = ep.formatValue(code, params.data);
      if (nonEmpty(lbl)) return lbl;
    } catch {}
  }

  const nm = params.data?.[`${colDef.field}_nm`];
  if (nonEmpty(nm)) return nm;

  const opts =
    ep?.values ?? ep?.options ?? ep?.list ?? ep?.items ?? ep?.dataSource;
  if (Array.isArray(opts) && opts.length) {
    const f = opts.find((o: any) =>
      [o?.value, o?.key, o?.cd, o?.code, o?.id].some((k) => k === code)
    );
    const lbl = f?.label ?? f?.text ?? f?.name ?? f?.nm ?? f?.ds;
    if (nonEmpty(lbl)) return lbl;
  }

  return code ?? "";
}

// --- SnCombo 패치: formatValue 강제 + valueGetter 라벨화 ---
function patchSnComboBoxColDef(defs: any[]) {
  defs.forEach((def) => {
    const isSnCombo =
      def?.cellEditor === "SnComboBox" ||
      def?.tp_column === "SnComboBox" ||
      def?.snType === "SnComboBox";
    if (!isSnCombo) return;

    if (!def.cellEditorParams) def.cellEditorParams = {};

    // ✅ formatValue가 없으면 반드시 주입 (_nm → 옵션 → 코드)
    if (typeof def.cellEditorParams.formatValue !== "function") {
      def.cellEditorParams.formatValue = (v: any, row?: any) => {
        const nm = row?.[`${def.field}_nm`];
        if (nm !== undefined && nm !== null && nm !== "") return nm;
        const opts =
          def.cellEditorParams.values ??
          def.cellEditorParams.options ??
          def.cellEditorParams.list ??
          def.cellEditorParams.items ??
          def.cellEditorParams.dataSource ??
          [];
        if (Array.isArray(opts) && opts.length) {
          const f = opts.find((o: any) =>
            [o?.value, o?.key, o?.cd, o?.code, o?.id].some((k) => k === v)
          );
          const lbl = f?.label ?? f?.text ?? f?.name ?? f?.nm ?? f?.ds;
          if (lbl !== undefined && lbl !== null && lbl !== "") return lbl;
        }
        return v ?? "";
      };
    }

    // 화면/엑셀 모두 라벨을 쓰도록
    def.valueGetter = (p: any) => getSnComboLabel(p, def);

    if (typeof def.valueSetter !== "function") {
      def.valueSetter = (p: any) => {
        p.data[def.field] = p.newValue; // 편집 가능 시 코드 저장
        return true;
      };
    }
  });
}

// --- SnCode 라벨 계산/패치 (이미 잘 되는 쪽) ---
function getSnCodeLabel(params: any, colDef: any) {
  const code = params.data?.[colDef.field];
  const dispKey = colDef?.cd_displaycolumn;
  if (dispKey) {
    const disp = params.data?.[dispKey];
    if (disp !== undefined && disp !== null && disp !== "") return disp;
  }
  const nm = params.data?.[`${colDef.field}_nm`];
  if (nm !== undefined && nm !== null && nm !== "") return nm;
  return code ?? "";
}

function patchSnCodeBoxColDef(defs: any[]) {
  defs.forEach((def) => {
    const isSnCode =
      def?.cellEditor === "SnCodeBox" ||
      def?.tp_column === "SnCodeBox" ||
      def?.snType === "SnCodeBox";
    if (!isSnCode) return;

    def.valueGetter = (p: any) => getSnCodeLabel(p, def);

    if (typeof def.valueSetter !== "function") {
      def.valueSetter = (p: any) => {
        p.data[def.field] = p.newValue;
        return true;
      };
    }
  });
}

// 컬럼 정의 생성 함수
export const createColumnDefs = async (
  columns: ColumnConfig[],
  isEditable: (field: string) => boolean,
  user: any,
  errorMap: Map<number, { fields: Set<string>; messages: Map<string, string> }>
): Promise<CustomColDef[]> => {
  const flatColumnDefs = await Promise.all(
    columns
      .sort((a, b) => a.no_seq - b.no_seq)
      .map(async (column) => {
        const typeConfig = await getColumnTypeConfig(column, user);

        const colDef: any = {
          headerName: column.nm_column,
          field: column.cd_column,
          aggFunc: column.yn_sum === "Y" ? "sum" : undefined,
          initialWidth: column.qt_width,
          width: column.qt_width,
          hide: column.fg_visible !== "Y",
          suppressColumnsToolPanel: column.fg_visible === "H",
          sortable: column.yn_allowsorting === "Y",
          pinned:
            column.yn_fixed === "Y" &&
            (column.tp_fixedposition === "left" ||
              column.tp_fixedposition === "right")
              ? (column.tp_fixedposition as "left" | "right")
              : null,
          editable: isEditable(column.cd_column),
          tp_column: column.tp_column,
          yn_required: column.yn_required,
          cd_clas: column.cd_clas,
          cd_popup: column.cd_popup,
          tp_format: column.tp_format,
          dc_editcon: column.dc_editcon,
          cd_displaycolumn: column.cd_displaycolumn,
          yn_sum: column.yn_sum,
          cd_user: column.cd_user,
          yn_drilldown: column.yn_drilldown,
          ...typeConfig,
          headerClass:
            column.yn_required === "Y"
              ? "required-header"
              : (column.headerClass ?? "normal-header"),
          ...(column.cellClass ? { cellClass: column.cellClass } : {}),
          suppressNavigable: (params: any) =>
            params.node.rowPinned === "bottom",
        };

        // 그룹명 추가 (나중에 묶을 때 사용)
        return { ...colDef, _group: column.nm_group ?? "" };
      })
  );

  const groupMap = new Map<string, any[]>();

  for (const colDef of flatColumnDefs) {
    const groupName = colDef._group?.trim();
    if (groupName) {
      if (!groupMap.has(groupName)) groupMap.set(groupName, []);
      groupMap.get(groupName)!.push(colDef);
    }
  }

  // no_seq 순서를 유지한 채 조립. 그룹 컬럼을 만나면 그 그룹이 처음 등장한
  // 자리에 그룹 헤더를 통째로 끼워넣고, 이후 같은 그룹 컬럼은 건너뛴다.
  // (ag-Grid는 그룹의 children이 배열상 연속이어야 하므로, no_seq가 떨어져 있는
  //  같은 그룹 컬럼은 첫 등장 위치로 모인다.)
  const groupedColumnDefs: any[] = [];
  const emittedGroups = new Set<string>();

  for (const colDef of flatColumnDefs) {
    const groupName = colDef._group?.trim();
    if (!groupName) {
      groupedColumnDefs.push(colDef);
      continue;
    }
    if (emittedGroups.has(groupName)) continue;
    emittedGroups.add(groupName);
    groupedColumnDefs.push({
      headerName: groupName,
      marryChildren: true,
      children: groupMap.get(groupName)!.map(({ _group, ...child }) => child),
    });
  }

  patchSnComboBoxColDef(groupedColumnDefs);
  patchSnCodeBoxColDef(groupedColumnDefs);
  return groupedColumnDefs;
};
